import { useState } from "react";

import { ArrowForwardIos } from "@mui/icons-material";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { exportReportFile } from "../../Api/ReportDetails/Report.Api";
import { ReactComponent as ExcelIcon } from "../../Assets/Images/Icon/ExcelIcon.svg";
import { ReactComponent as PdfIcon } from "../../Assets/Images/Icon/PDFIcon.svg";
import { Heading1 } from "../../Components/atoms/Typography/Heading/Heading";
import ReportActionButton from "../../Components/molecules/ActionButton/ReportActionButton.js";
import { REPORTS } from "../../Constants/ReportTypes";
import {
  downloadReportExport,
  getReportExportErrorMessage,
  normalizeExportFileType,
} from "../../Utils/FileDownload/FileDownload";

export const Reports = () => {
  const [error, setError] = useState(null);
  const [activeExport, setActiveExport] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getExportErrorMessage = (message) =>
    getReportExportErrorMessage(message, {
      noRecordsMessage: t("validation:report_no_records"),
      serverErrorMessage: t("validation:server-error"),
    });

  const isExportLoading = (reportId, fileType) =>
    activeExport?.reportId === reportId && activeExport?.fileType === fileType;

  const handleReportDownload = async (reportNumber, reportName, fileType) => {
    setActiveExport({ reportId: reportNumber, fileType });
    setError(null);

    try {
      const backendFileType = normalizeExportFileType(fileType);
      const response = await exportReportFile({
        reportName,
        fileType: backendFileType,
        doctors: reportName === "Patient Demographic Analysis" ? "ALL" : null,
      });

      if (response?.type === 0 && response?.data) {
        downloadReportExport(response, {
          reportName,
          fileType: backendFileType,
        });
        setError(null);
        return;
      }

      setError(getExportErrorMessage(response?.message));
    } catch (err) {
      setError(getExportErrorMessage(err?.message));
    } finally {
      setActiveExport(null);
    }
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Stack direction="row" justifyContent="space-between" mb={0.8} mt={2}>
          <Heading1 text={"Reports"} />
        </Stack>
      </Grid>
      {REPORTS.map((report, index) => (
        <Grid
          item
          key={report.id}
          sx={{ mt: index === 0 ? 3 : 0, width: "100%" }}
        >
          <Paper
            sx={{
              padding: "8px 12px",
              boxShadow: "0px 0px 25px rgba(0, 0, 0, 0.05)",
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: { xs: "100%", sm: "90%", md: "80%", lg: "80%" },
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
              sx={{
                width: "100%",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                "@media (max-width: 980px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "5px",
                },
              }}
            >
              {/* Title */}
              <Typography
                title={report.name}
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  color: "#000",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  paddingLeft: "10px",
                }}
              >
                {report.name}
              </Typography>

              {/* Buttons */}
              {report.exceldownload || report.pdfdownload ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  spacing={1.5}
                  sx={{
                    width: { xs: "100%", sm: "45%" },
                    "@media (max-width: 980px)": {
                      width: "100%",
                      flexDirection: "column",
                    },
                  }}
                >
                  {report.pdfdownload && (
                    <ReportActionButton
                      loading={isExportLoading(report.id, "pdf")}
                      onClick={() =>
                        handleReportDownload(report.id, report.name, "pdf")
                      }
                      icon={<PdfIcon type="img" />}
                      label="Generate PDF"
                    />
                  )}

                  {report.exceldownload && (
                    <ReportActionButton
                      loading={isExportLoading(report.id, "excel")}
                      onClick={() =>
                        handleReportDownload(report.id, report.name, "excel")
                      }
                      icon={<ExcelIcon type="img" />}
                      label="Generate Excel"
                    />
                  )}
                </Stack>
              ) : (
                /* Arrow button */
                <Button
                  sx={{
                    minWidth: 0,
                    width: "35px",
                    height: "35px",
                    borderRadius: "5px",
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() => {
                    const reportName = report.name
                      .replace(/\s+/g, "")
                      .toLowerCase();
                    navigate(`/reports/${reportName}`, {
                      state: { reportNo: report.id, reportsName: report.name },
                    });
                  }}
                  disabled={activeExport?.reportId === report.id}
                >
                  <ArrowForwardIos size={20} />
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      ))}
      {error && (
        <Grid item>
          <div style={{ color: "red", textAlign: "center" }}>{error}</div>
        </Grid>
      )}
    </Grid>
  );
};
